import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ApiError } from '../api/client'
import { initials } from '../app/initials'
import { STATUS_LABEL } from '../orders/labels'
import { formatDateDE } from '../time/format'
import { CallButtonLink } from './CallLink'
import { CustomerDsgvo } from './CustomerDsgvo'
import { CustomerForm, type CustomerFields } from './CustomerForm'
import { useCustomer, usePatchCustomer, usePatchVehicle } from './useCustomers'
import { VehicleCard } from './VehicleCard'
import { VehicleCreateForm } from './VehicleCreateForm'
import { VehicleEditForm } from './VehicleEditForm'

/** Rechte Seite des Master-Detail: Stammdaten (editierbar), Fahrzeuge (anlegen/bearbeiten/löschen/
 *  anonymisieren/Historie), Auftragshistorie und DSGVO-Aktionen. */
export function CustomerDetailPane({ id, onDeleted }: { id: number; onDeleted: () => void }) {
  const { data, isLoading, error } = useCustomer(id)
  const patch = usePatchCustomer(id)
  const patchVehicle = usePatchVehicle(id)
  const [editingVehicleId, setEditingVehicleId] = useState<number | null>(null)
  const [addingVehicle, setAddingVehicle] = useState(false)

  if (isLoading) return <div className="muted">Kunde wird geladen…</div>
  if (error || !data) {
    return (
      <div className="error" role="alert">
        Kunde konnte nicht geladen werden.
      </div>
    )
  }

  const { customer, vehicles, orders } = data
  const initialFields: CustomerFields = {
    name: customer.name,
    phone: customer.phone ?? '',
    email: customer.email ?? '',
    address: customer.address ?? '',
  }

  // 409 = OCC-Versionskonflikt — die Mutation invalidiert bereits, der frische Stand lädt nach.
  const conflict = patch.error instanceof ApiError && patch.error.status === 409
  const errorMsg = conflict
    ? 'Zwischenzeitlich geändert – der aktuelle Stand wurde neu geladen.'
    : patch.error instanceof ApiError
      ? patch.error.message
      : null

  const vehicleConflict = patchVehicle.error instanceof ApiError && patchVehicle.error.status === 409
  const vehicleErrorMsg = vehicleConflict
    ? 'Zwischenzeitlich geändert – der aktuelle Stand wurde neu geladen.'
    : patchVehicle.error instanceof ApiError
      ? patchVehicle.error.message
      : null

  return (
    <>
      <div className="wf-detailhead">
        <span className="wf-listrow__avatar" aria-hidden="true">
          {initials(customer.name)}
        </span>
        <div className="wf-detailhead__main">
          <div className="wf-detailhead__title">{customer.name}</div>
          {customer.phone || customer.email ? (
            <div className="wf-card__meta">
              {customer.phone ?? ''}
              {customer.phone && customer.email ? ' · ' : ''}
              {customer.email ?? ''}
            </div>
          ) : null}
        </div>
        {customer.phone ? <CallButtonLink phone={customer.phone} name={customer.name} /> : null}
      </div>

      <CustomerForm
        key={customer.id}
        initial={initialFields}
        submitLabel="Speichern"
        submitting={patch.isPending}
        error={errorMsg}
        onSubmit={(fields) =>
          patch.mutate({
            version: customer.version,
            name: fields.name,
            phone: fields.phone || null,
            email: fields.email || null,
            address: fields.address || null,
          })
        }
      />

      <div className="wf-sectionhead">
        <h3>Fahrzeuge</h3>
        <span className="wf-count">{vehicles.length}</span>
        <button
          type="button"
          className="wf-btn wf-btn--ghost wf-sectionhead__action"
          aria-expanded={addingVehicle}
          onClick={() => setAddingVehicle((open) => !open)}
        >
          ＋ Fahrzeug
        </button>
      </div>
      {addingVehicle ? <VehicleCreateForm customerId={id} onDone={() => setAddingVehicle(false)} /> : null}
      {vehicles.length === 0 ? (
        <div className="wf-empty">
          <div className="wf-empty__title">Keine Fahrzeuge</div>
          <div className="wf-empty__text">Über „＋ Fahrzeug" anlegen — oder sie entstehen mit neuen Aufträgen.</div>
        </div>
      ) : (
        vehicles.map((vehicle) =>
          editingVehicleId === vehicle.id ? (
            <VehicleEditForm
              key={vehicle.id}
              vehicle={vehicle}
              busy={patchVehicle.isPending}
              error={vehicleErrorMsg}
              onSave={(fields) =>
                patchVehicle.mutate(
                  { vehicleId: vehicle.id, body: { version: vehicle.version, ...fields } },
                  { onSuccess: () => setEditingVehicleId(null) },
                )
              }
              onCancel={() => setEditingVehicleId(null)}
            />
          ) : (
            <VehicleCard key={vehicle.id} vehicle={vehicle} customerId={id} onEdit={() => setEditingVehicleId(vehicle.id)} />
          ),
        )
      )}

      <div className="wf-sectionhead">
        <h3>Auftragshistorie</h3>
        <span className="wf-count">{orders.length}</span>
      </div>
      {orders.length === 0 ? (
        <div className="wf-empty">
          <div className="wf-empty__title">Noch keine Aufträge</div>
          <div className="wf-empty__text">Alle Aufträge dieses Kunden erscheinen hier.</div>
        </div>
      ) : (
        orders.map((order) => (
          <Link key={order.id} to={`/orders/${order.id}`} className="wf-card">
            <div className="wf-card__row">
              <span className="wf-card__nr">{order.nr ?? order.id}</span>
              <span className={`wf-pill wf-pill--${order.status}`}>{STATUS_LABEL[order.status] ?? order.status}</span>
            </div>
            <div className="wf-card__meta">{formatDateDE(order.createdAt)}</div>
          </Link>
        ))
      )}

      <CustomerDsgvo id={customer.id} name={customer.name} onDeleted={onDeleted} />
    </>
  )
}
